import cn from 'classnames'
import { LYSDivider, LYSTypography } from '@core/components/Primitives'
import LYSStaticImage from '@core/components/Primitives/LYSStaticImage'
import { getGhsClassImage } from '@core/config/config.helper'
import { useTranslation } from '@core/i18n/i18n'
import {
  getDangerSignalWord,
  getHazardStatementPhrase,
  getPrecautionaryStatementPhrase,
} from '@core/utils/models/ghs'
import { useProductDetailContext } from '@core/utils/models/product/ProductContext'
import { RenderProp, Slot, template } from '@core/utils/templates'
import style from './index.module.less'

export interface HazardWarningsSlots {
  header: RenderProp<void>
  dangerHint: RenderProp<void>
  hazardStatements: RenderProp<void>
  safetyHint: RenderProp<void>
  precautionaryStatements: RenderProp<void>
  biocideStatements: RenderProp<void>
}

export interface HazardWarningsProps {
  className: string
  withBottomDivider?: boolean
}

const HazardWarnings = template<HazardWarningsProps, HazardWarningsSlots>(
  ({ slots, className, withBottomDivider }) => {
    const { t } = useTranslation()
    const { product } = useProductDetailContext() || {}

    if (!product) {
      return null
    }

    const {
      ghsClasses,
      hazardStatements,
      precautionaryStatements,
      biocideProductTypes,
    } = product

    if (
      ghsClasses.length === 0 &&
      hazardStatements.length === 0 &&
      precautionaryStatements.length === 0 &&
      biocideProductTypes.length === 0
    ) {
      return null
    }

    const showDangerSignalWord = hazardStatements.length > 0
    const showSafetyHint =
      precautionaryStatements.length > 0 || biocideProductTypes.length > 0

    return (
      <div className={cn(style.hazardWarnings, className)}>
        <Slot render={slots?.header}>
          <LYSTypography.Title level={4}>
            {t('productDetail.hazardWarnings.title')}
          </LYSTypography.Title>
        </Slot>
        <Slot render={slots?.hazardStatements}>
          {ghsClasses.map((ghsClass) => {
            const image = getGhsClassImage(ghsClass.code)

            if (image !== false) {
              return (
                <LYSStaticImage
                  className={style.ghsImage}
                  alt={ghsClass.label[0]}
                  key={ghsClass.code}
                  src={image}
                  width={50}
                  height={50}
                />
              )
            }
          })}
        </Slot>
        <Slot render={slots?.dangerHint}>
          {showDangerSignalWord ? (
            <LYSTypography.Paragraph strong>
              {getDangerSignalWord(product)}
            </LYSTypography.Paragraph>
          ) : null}
        </Slot>
        <Slot render={slots?.precautionaryStatements}>
          {hazardStatements.length > 0 ? (
            <ul>
              {hazardStatements.map((statement) => (
                <li key={statement.code} className={style.hazardDescription}>
                  {getHazardStatementPhrase(statement)}
                </li>
              ))}
            </ul>
          ) : null}
        </Slot>
        <Slot render={slots?.safetyHint}>
          {showSafetyHint ? (
            <LYSTypography.Paragraph strong>
              {t('productDetail.hazardWarnings.safetyHint')}
            </LYSTypography.Paragraph>
          ) : null}
        </Slot>
        <Slot render={slots?.precautionaryStatements}>
          {precautionaryStatements.length > 0 ? (
            <ul style={{ marginLeft: '-10px' }}>
              {precautionaryStatements.map((statement) => (
                <li
                  className={style.precautionaryStatements}
                  key={statement.code}
                  style={{
                    // maxWidth: '200px',
                    listStylePosition: 'inside',
                    // whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getPrecautionaryStatementPhrase(statement)}
                </li>
              ))}
            </ul>
          ) : null}
        </Slot>
        <Slot render={slots?.biocideStatements}>
          {biocideProductTypes.length > 0 ? (
            <p className={style.biocideNoticeParagraph}>
              {t('productDetail.hazardWarnings.biocideProductHandling')}
            </p>
          ) : null}
        </Slot>
        {withBottomDivider && <LYSDivider />}
      </div>
    )
  }
)

export default HazardWarnings
